/**
 * Internal Hook: useTOSClearMutation
 *
 * Mutation for clearing/removing TOS acceptance from backend.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createRetryFn } from '../../../config/react-query';
import { useApiManager } from '../../../hooks';
import { useUser } from '../../UserContext';
import { useUserPermissions } from '../../UserPermissionsContext';
import { SETTINGS_QUERY_KEYS } from '../helpers';

export const useTOSClearMutation = () => {
  const apiManager = useApiManager();
  const { isSuccess: isUserPermissionsReady } = useUserPermissions();
  const { idToken } = useUser();
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!(isUserPermissionsReady && idToken && apiManager)) {
        console.warn(
          '[TOSClearMutation] API Manager not initialized or no token, skipping backend clear',
        );
        return;
      }

      // Ensure apiManager has the latest token before making the call
      apiManager.setAuthToken(idToken);

      await apiManager.profile.clearTosAcceptance();
    },
    onSuccess: () => {
      // Update the cache to null (no acceptance)
      queryClient.setQueryData(SETTINGS_QUERY_KEYS.tos, null);
    },
    onError: (error) => {
      console.error('[SettingsContext] Failed to clear TOS acceptance:', error);
    },
    retry: createRetryFn({ maxRetries: 2 }),
  });
};
